import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import { VerificationResult } from '../interfaces/core/ArticleVerification';

interface VerificationResultsTableProps {
  results: VerificationResult[];
  totalChecked?: number;
  totalMissing?: number;
}

const VerificationResultsTable: React.FC<VerificationResultsTableProps> = ({
  results,
  totalChecked,
  totalMissing,
}) => {
  if (!results || results.length === 0) {
    return null;
  }

  return (
    <Box>
      {(totalChecked !== undefined || totalMissing !== undefined) && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Total Checked: {totalChecked ?? 0} | Missing: {totalMissing ?? 0}
        </Typography>
      )}
      <TableContainer 
        component={Paper} 
        sx={{ 
          mt: 2,
          maxHeight: 420,
          '& .MuiTableCell-root': {
            px: 2, 
            py: 1,
          }
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Article Number</TableCell>
              <TableCell>Internal Number</TableCell>
              <TableCell>Related Number</TableCell>
              <TableCell>Supplier ID</TableCell>
              <TableCell>Manufacturer</TableCell>
              <TableCell>TecDoc Status</TableCell>
              <TableCell>Database Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((result, index) => (
              <TableRow
                key={`${result.articleNumber}-${result.dataSupplierId}-${index}`}
              >
                <TableCell>{result.articleNumber}</TableCell>
                <TableCell>{result.internalNumber || "-"}</TableCell>
                <TableCell>{result.related_number || "-"}</TableCell>
                <TableCell>{result.dataSupplierId}</TableCell>
                <TableCell>{result.mfrName || "-"}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {result.tecdoc_exists ? (
                      <>
                        <CheckCircleOutlineIcon color="success" />
                        <Typography color="success.main">
                          Found in TecDoc
                        </Typography>
                      </>
                    ) : (
                      <>
                        <ErrorOutlineIcon color="error" />
                        <Tooltip 
                          title="This article does not exist in TecDoc. This could mean: 1) The product no longer exists in TecDoc, or 2) The Article Number and Data Supplier ID provided in the Excel file are incorrect."
                          arrow
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography color="error">
                              Not in TecDoc
                            </Typography>
                            <InfoIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          </Box>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {result.exists ? (
                      <>
                        <CheckCircleOutlineIcon color="success" />
                        <Typography color="success.main">
                          Found in Database
                        </Typography>
                      </>
                    ) : (
                      <>
                        <ErrorOutlineIcon color="error" />
                        <Tooltip 
                          title="This article does not exist in the database. This could mean: 1) The article has never been imported into the database, or 2) The Article Number and Data Supplier ID in the Excel file are different from what's stored in the database. Update Article in ERP and tomorrow will be updated in the catalogue."
                          arrow
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography color="error">
                              Not in Database
                            </Typography>
                            <InfoIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          </Box>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default VerificationResultsTable;

